import pdfMake from 'pdfmake/build/pdfmake';
import CrystalService from 'business/services/CrystalService';
import { SynchTripReportOptionsProps } from 'components/synchrotron/constants';
import {
  Cryoprotectantv4,
  CrystalDTO,
  Soakingv4,
  SynchTripDTO,
} from 'business/models/Models';
import { getDateISOFormat, roundStringValue } from 'utils/Global';
import { getReportHeader } from 'components/synchrotron/utils';
import { createPdfWithPopupBlockerHandler } from './utils';

const crystalService = new CrystalService();

export const SynchrotronTripReport = async (
  synchTrips: SynchTripDTO[],
  reportOptions?: SynchTripReportOptionsProps,
  saveReport?: boolean,
  selectedAssignedCrystalIds?: number[],
  onPopupBlocked?: () => void,
) => {
  const pdfDefinition = {
    info: {
      title: 'Synchrotron Trip Report',
      author: 'Rock Maker',
      subject: 'Synchrotron Trip',
    },
    content: await GetContent(reportOptions, synchTrips, selectedAssignedCrystalIds),
    styles: {
      header: {
        fontSize: 18,
        bold: true,
      },
      subheader: {
        fontSize: 15,
        bold: true,
      },
      bigger: {
        fontSize: 15,
        italics: true,
      },
      subheading: {
        color: '#555555',
      },
      center: {
        alignment: 'center',
      },
      noMargin: { margin: [0, 0, 0, 0] },
      h1: {
        fontSize: 12,
        bold: true,
      },
      h2: {
        fontSize: 11,
        bold: true,
      },
      h3: {
        fontSize: 10,
        bold: true,
      },
      cellRight: {
        alignment: 'right',
      },
    },
    defaultStyle: {
      columnGap: 5,
      lineHeight: 0.8,
      fontSize: 10,
    },
    pageMargins: [50, 50, 50, 30],
    header: getReportHeader(),
    pageBreakBefore: false,
  };
  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    `SynchrotronTripReport_${synchTrips[0].synchTripName.replace(
      /\W/g,
      '_',
    )}_${getDateISOFormat(new Date())}`,
    saveReport,
    onPopupBlocked,
  );
};

const GetContent = async (
  reportOptions: SynchTripReportOptionsProps,
  synchTrips: SynchTripDTO[],
  selectedAssignedCrystalIds?: number[],
) => {
  const reports = [];
  await Promise.allSettled(
    synchTrips?.map(async (synchTrip, index) => {
      const report = await CreateContent(
        reportOptions,
        synchTrip,
        selectedAssignedCrystalIds,
      );
      reports.push(report);
    }),
  );
  return reports;
};

const CreateContent = async (
  reportOptions: SynchTripReportOptionsProps,
  synchTrip: SynchTripDTO,
  selectedAssignedCrystalIds?: number[],
) => {
  const content = [];
  if (reportOptions.tripInfoReport) {
    content.push(await SynchTripInfo(synchTrip));
    content.push('\n');
    content.push(await getAssignedCrystalsHeading());
    if (!synchTrip?.assignedCrystals?.length) {
      content.push(await getNoAssignedCrystalsHeading());
      content.push('\n');
    }
    for (const crystal of synchTrip.assignedCrystals) {
      if (
        !selectedAssignedCrystalIds?.length ||
        selectedAssignedCrystalIds.includes(crystal.id)
      ) {
        content.push(await getCrystalInfo(crystal));
        content.push('\n');

        if (reportOptions.tripResultReport) {
          content.push(await getCrystalResult(crystal));
          content.push('\n');
        }

        content.push(await getCrystalProteinInfo(crystal));
        content.push('\n');

        content.push(await getCrystalPuckDewarInfo(crystal));
        content.push('\n');

        content.push(
          await getCrystalIngredientsInfo(crystal.cryoprotectants, crystal.soakings),
        );
        content.push('\n');

        content.push(await getCrystalNotes(crystal.notes));
        content.push(await getDivider());
        content.push('\n');
      }
    }
  }
  return content;
};

const SynchTripInfo = async (synchTrip: SynchTripDTO) => {
  return [
    {
      fillColor: '#F7F7F7',
      table: {
        widths: ['*'],
        body: [
          [
            {
              text: 'Synchrotron Trip Information',
              style: 'h1',
              margin: [1, 1, 1, 5],
            },
          ],
          [
            {
              table: {
                widths: ['25%', '25%', '25%', '25%'],
                body: [
                  [
                    { text: 'Synchrotron Trip Name', style: 'subheading' },
                    { text: synchTrip.synchTripName },
                    { text: 'Detector Type', style: 'subheading' },
                    { text: synchTrip.detectorType },
                  ],
                  [
                    { text: 'Data Collection Date', style: 'subheading' },
                    {
                      text: synchTrip.dataCollectionDate.toLocaleDateString('en-US'),
                    },
                    { text: 'Contact Person', style: 'subheading' },
                    { text: synchTrip.contactPerson },
                  ],
                  [
                    { text: 'Beamline', style: 'subheading' },
                    { text: synchTrip.beamLine },
                    { text: 'Phone Number', style: 'subheading' },
                    { text: synchTrip.phoneNumber },
                  ],
                  [
                    { text: 'Notes', style: 'subheading' },
                    {
                      text: synchTrip.notes,
                      colSpan: 3,
                    },
                  ],
                ],
              },
              layout: 'noBorders',
            },
          ],
        ],
      },
      layout: {
        hLineWidth: function (i, node) {
          return i === 0 || i === node.table.body.length ? 0.1 : 0.1;
        },
        vLineWidth: function (i, node) {
          return i === 0 || i === node.table.widths.length ? 0.1 : 0;
        },
      },
    },
  ];
};

const getDivider = async () => {
  return {
    canvas: [{ type: 'line', x1: 0, y1: 5, x2: 575 - 2 * 40, y2: 5, lineWidth: 0.1 }],
  };
};

const getAssignedCrystalsHeading = async () => {
  return [{ text: 'Assigned Crystal Information', style: 'h1' }, await getDivider()];
};

const getNoAssignedCrystalsHeading = async () => {
  return [
    { text: 'No Crystals assigned.', style: ['subheading', 'center'] },
    await getDivider(),
  ];
};

const getCrystalInfo = async (crystal: CrystalDTO) => {
  const img = await crystalService.getCrystalImage(crystal.id);
  return [
    {
      columns: [
        {
          table: {
            widths: ['40%', '60%'],
            heights: 12,
            body: [
              [{ text: 'Crystal ID', style: 'subheading' }, { text: crystal.id }],
              [{ text: 'Crystal Name', style: 'subheading' }, { text: crystal.name }],
              [{ text: 'Project', style: 'subheading' }, { text: crystal.projectName }],
              [
                { text: 'Experiment', style: 'subheading' },
                { text: crystal.experimentName },
              ],
              [
                { text: 'Protein Name', style: 'subheading' },
                { text: crystal.proteinName },
              ],
              [{ text: 'Owner', style: 'subheading' }, { text: crystal.ownerName }],
              [
                { text: 'Barcode, Well, & Drop', style: 'subheading' },
                { text: `${crystal.barcode}, ${crystal.wellRowCol}` },
              ],
              [
                { text: 'Date Collected', style: 'subheading' },
                { text: crystal.dateCollected?.toLocaleDateString() ?? '-' },
              ],
              [
                { text: 'Date Frozen', style: 'subheading' },
                { text: crystal.dateFrozen?.toLocaleDateString() ?? '-' },
              ],
            ],
          },
          layout: 'noBorders',
        },
        {
          table: {
            widths: ['100%'],

            headerRows: 1,
            body: [
              [
                img.image
                  ? {
                      image: img.image.toString(),
                      fit: [230, 130],
                      style: 'cellRight',
                    }
                  : {
                      text: 'Image Not Found',
                      alignment: 'center',
                      margin: [0, 40, 0, 40],
                      style: 'cellRight',
                      color: 'gray',
                    },
              ],
            ],
          },
          layout: 'noBorders',
        },
      ],
    },
  ];
};

const getCrystalProteinInfo = async (crystal: CrystalDTO) => {
  return [
    { text: 'Protein Information', style: 'h2' },
    '\n',
    {
      table: {
        widths: ['20%', '80%'],
        body: [
          [
            { text: 'Protein Formulation', style: 'subheading' },
            { text: crystal.proteinName },
          ],
          [
            { text: 'Protein Volume', style: 'subheading' },
            {
              text: crystal.proteinFormulationDTO.volume
                ? crystal.proteinFormulationDTO.volume + ' µL'
                : '',
            },
          ],
          [
            { text: 'Protein Ingredients', style: 'subheading' },
            {
              text: crystal.proteinFormulationDTO.proteinFormulationIngredient?.map(
                (pi) =>
                  `${pi.name} ${pi.concentrationmM} mM ${pi.concentrationmgmL} mg/mL\n`,
              ),
            },
          ],
          [
            { text: 'Standard Ingredients', style: 'subheading' },
            {
              text: crystal.proteinFormulationDTO.proteinFormulationStandardIngredient?.map(
                (si) =>
                  `${si.ingredientName} ${si.concentration} ${
                    si.stockConcentrationUnit
                  } ${si.pH ? si.pH + 'pH' : ''}\n`,
              ),
            },
          ],
        ],
      },
      layout: 'noBorders',
    },
  ];
};

const getCrystalPuckDewarInfo = async (crystal: CrystalDTO) => {
  return [
    {
      table: {
        widths: ['50%', '50%'],
        body: [
          [
            { text: 'Puck', style: 'h3' },
            { text: 'Dewar', style: 'h3' },
          ],
          [
            {
              text: `${
                crystal?.puckDewar
                  ? crystal?.puckDewar?.puckName +
                    ' - ' +
                    crystal?.puckDewar?.puckType +
                    ' - ' +
                    crystal?.puckDewar?.puckPosition
                  : ''
              }`,
            },
            { text: crystal?.puckDewar?.dewarName },
          ],
        ],
      },
      layout: 'noBorders',
    },
  ];
};

const getCrystalIngredientsInfo = async (
  cryoprotectants: Cryoprotectantv4[],
  soakings: Soakingv4[],
) => {
  return [
    {
      table: {
        widths: ['50%', '50%'],
        body: [
          [
            { text: 'Cryoprotectant', style: 'h3' },
            { text: 'Soaking', style: 'h3' },
          ],
          [
            {
              text: cryoprotectants?.map(
                (c) =>
                  `${c.ingredient} ${roundStringValue(c.conc)} ${c.concUnit} ${
                    c.ph ? c.ph : ''
                  } ${c.protocol ? c.protocol : ''}\n`,
              ),
            },
            {
              text: soakings?.map(
                (s) =>
                  `${s.ingredient} ${roundStringValue(s.conc)} ${s.concUnit} ${
                    s.time + s.timeUnit
                  } ${s.textInfo}\n`,
              ),
            },
          ],
        ],
      },
      layout: 'noBorders',
    },
  ];
};

const getCrystalNotes = async (notes: string) => {
  return [
    { text: 'Notes', style: 'h2' },
    {
      text: notes,
    },
  ];
};

const getCrystalResult = async (crystal: CrystalDTO) => {
  return [
    { text: 'Crystal Result', style: 'h2' },
    '\n',
    {
      columns: [
        {
          table: {
            widths: ['40%', '60%'],
            body: [
              [
                { text: 'Crystal Name (Internal)', style: 'subheading' },
                { text: crystal?.result?.crystalNameInternal },
              ],
              [
                { text: 'Date Frozen', style: 'subheading' },
                { text: crystal?.dateFrozen?.toLocaleDateString('en-US') },
              ],
              [{ text: 'PDB ID', style: 'subheading' }, { text: crystal?.result?.e21 }],
              [
                { text: 'Symm/Space Group', style: 'subheading' },
                { text: crystal?.result?.symm },
              ],
              [
                { text: 'xtal_run', style: 'subheading' },
                { text: crystal?.result?.xtalRun },
              ],
              [
                { text: 'Reso_lo/hi_shells', style: 'subheading' },
                {
                  columns: [
                    { text: `lo: ${crystal?.result?.resoLow ?? ''}` },
                    { text: `hi: ${crystal?.result?.hiShells ?? ''}` },
                  ],
                },
              ],
              [
                { text: 'cell', style: 'subheading' },
                {
                  columns: [
                    {
                      text: `a: ${
                        crystal?.result?.cellA ? crystal?.result?.cellA + ' Å' : ''
                      }`,
                    },
                    {
                      text: `b: ${
                        crystal?.result?.cellB ? crystal?.result?.cellB + ' Å' : ''
                      }`,
                    },
                    {
                      text: `c: ${
                        crystal?.result?.cellC ? crystal?.result?.cellC + ' Å' : ''
                      }`,
                    },
                  ],
                },
              ],
              [
                { text: '', style: 'subheading' },
                {
                  columns: [
                    {
                      text: `α: ${
                        crystal?.result?.cellAlpha ? crystal?.result?.cellAlpha + '°' : ''
                      }`,
                    },
                    {
                      text: `β: ${
                        crystal?.result?.cellBeta ? crystal?.result?.cellBeta + '°' : ''
                      }`,
                    },
                    {
                      text: `γ: ${
                        crystal?.result?.cellGamma ? crystal?.result?.cellGamma + '°' : ''
                      }`,
                    },
                  ],
                },
              ],
              [
                { text: 'R, R-free', style: 'subheading' },
                { text: crystal?.result?.raddamRfree },
              ],
              [
                { text: '%comp_ell', style: 'subheading' },
                { text: crystal?.result?.compEll },
              ],
              [
                { text: '%comp_sph', style: 'subheading' },
                { text: crystal?.result?.compSph },
              ],
            ],
          },
          layout: 'noBorders',
        },
        {
          table: {
            widths: ['40%', '60%'],
            body: [
              [{ text: '%Rmeas', style: 'subheading' }, { text: crystal?.result?.rmeas }],
              [{ text: 'Fo-Fc', style: 'subheading' }, { text: crystal?.result?.foFc }],
              [{ text: 'CC1/2', style: 'subheading' }, { text: crystal?.result?.cc12 }],
              [
                { text: 'Multiplicity', style: 'subheading' },
                { text: crystal?.result?.multiplicity },
              ],
              [
                { text: '|/sigmaI', style: 'subheading' },
                { text: crystal?.result?.isigma },
              ],
              [
                { text: 'Refinement Program', style: 'subheading' },
                { text: crystal?.result?.refinementProgram },
              ],
              [
                { text: 'Notes', style: 'subheading' },
                {
                  text: crystal?.result?.resultNotes,
                },
              ],
            ],
          },
          layout: 'noBorders',
        },
      ],
    },
  ];
};

export default SynchrotronTripReport;
